import { Info } from 'lucide-preact'
import { Select } from '@/components/common/Select'
import { RadioGroup } from '@/components/common/RadioGroup'
import { TextBox } from '@/components/common/TextBox'
import { ToggleRow } from '@/components/common/ToggleRow'
import type { HubSpotMappingConfig, HubSpotUtmMode } from '@/types/hubspot'
import {
    LEAD_SOURCE_PROPERTY,
    LEAD_SOURCE_PROPERTY_LABEL,
    findLeadSourceMapping,
    isLeadSourceProperty,
    withLeadSourceMapping,
} from './leadSource'

interface NewLeadBehaviorStepProps {
    config: HubSpotMappingConfig
    onChange: (config: HubSpotMappingConfig) => void
    /** Opens the Field Mapping step, where the lead source can be pointed at the client's own property. */
    onEditFieldMapping?: () => void
}

export function NewLeadBehaviorStep({ config, onChange, onEditFieldMapping }: NewLeadBehaviorStepProps) {
    const behavior = config.NewLeadBehavior
    const leadSource = findLeadSourceMapping(config)
    const leadSourceOn = !!leadSource?.IsEnabled && !!leadSource.HubSpotProperty
    const writesOurProperty = isLeadSourceProperty(leadSource?.HubSpotProperty)
    // The sync writes nothing for an empty value, so the card must not say it does.
    const hasValue = !!leadSource?.FixedValue?.trim()

    const updateBehavior = (updates: Partial<typeof behavior>) => {
        onChange({ ...config, NewLeadBehavior: { ...behavior, ...updates } })
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-xl font-semibold text-slate-900 dark:text-white">New Lead Behavior</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Configure actions when a matching lead is not found in HubSpot.
                </p>
            </div>

            {/* Info banner */}
            <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-xs text-blue-700 dark:text-blue-300">
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <p>When no matching lead exists, create a new contact in HubSpot. We will use the email address provided to check for existing records before creating a new one.</p>
            </div>

            {/* Which fields are written */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-3">
                <RadioGroup
                    label="Which fields are written"
                    name="writeMode"
                    helperText="Select the default mapping behavior for new contacts."
                    options={[
                        { value: 'MappedFieldsOnly', label: 'Write mapped fields only' },
                        { value: 'MappedFieldsAndStandardData', label: 'Write mapped fields and standard OrbitalX data' },
                    ]}
                    value={behavior.WriteMode}
                    onChange={(val) => updateBehavior({ WriteMode: val as typeof behavior.WriteMode })}
                />
            </div>

            {/* Lifecycle stage + Lead status */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                    <Select
                        label="Lifecycle stage default"
                        name="defaultLifecycleStage"
                        options={[
                            { value: 'subscriber', label: 'Subscriber' },
                            { value: 'lead', label: 'Lead' },
                            { value: 'marketingqualifiedlead', label: 'Marketing Qualified Lead' },
                            { value: 'salesqualifiedlead', label: 'Sales Qualified Lead' },
                            { value: 'opportunity', label: 'Opportunity' },
                            { value: 'customer', label: 'Customer' },
                        ]}
                        defaultValue={behavior.DefaultLifecycleStage}
                        onChange={(e: Event) => updateBehavior({ DefaultLifecycleStage: (e.target as HTMLSelectElement).value })}
                    />
                </div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5">
                    <Select
                        label="Lead status default"
                        name="defaultLeadStatus"
                        options={[
                            { value: 'NEW', label: 'New' },
                            { value: 'OPEN', label: 'Open' },
                            { value: 'IN_PROGRESS', label: 'In Progress' },
                            { value: 'ATTEMPTED_TO_CONTACT', label: 'Attempted to Contact' },
                        ]}
                        defaultValue={behavior.DefaultLeadStatus}
                        onChange={(e: Event) => updateBehavior({ DefaultLeadStatus: (e.target as HTMLSelectElement).value })}
                    />
                </div>
            </div>

            {/* Lead source: the Lead Source field mapping's fixed value, on new contacts only (ORB-1056) */}
            <div>
                <div className="rounded-xl border border-slate-200 dark:border-slate-700">
                    <div className="p-5 space-y-4">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h4 className="font-medium text-sm text-slate-900 dark:text-white">Lead source</h4>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5 max-w-[52ch]">
                                    Recorded on each new contact, so your sales team can see where the lead came from.
                                </p>
                            </div>
                            <ToggleRow
                                checked={leadSourceOn}
                                onChange={() => onChange(withLeadSourceMapping(config, leadSourceOn
                                    ? { IsEnabled: false }
                                    : { IsEnabled: true, HubSpotProperty: leadSource?.HubSpotProperty || LEAD_SOURCE_PROPERTY }))}
                                ariaLabel="Record a lead source"
                            />
                        </div>
                        {leadSourceOn && (
                            <TextBox
                                label="Lead source value"
                                name="leadSourceValue"
                                value={leadSource?.FixedValue ?? ''}
                                onChange={(next) => onChange(withLeadSourceMapping(config, { FixedValue: next }))}
                            />
                        )}
                    </div>
                    <div className="border-t border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/50 rounded-b-xl px-5 py-3 text-xs leading-relaxed text-slate-600 dark:text-slate-300">
                        {leadSourceOn && !hasValue ? (
                            <p>Enter a lead source value. Without one, nothing is written.</p>
                        ) : leadSourceOn ? (
                            <>
                                <p>
                                    Written to{' '}
                                    <span className="font-semibold text-slate-900 dark:text-white">
                                        {writesOurProperty ? LEAD_SOURCE_PROPERTY_LABEL : leadSource?.HubSpotProperty}
                                    </span>{' '}
                                    {writesOurProperty && <code className="text-slate-500 dark:text-slate-400">{LEAD_SOURCE_PROPERTY}</code>}{' '}
                                    on <span className="font-semibold text-slate-900 dark:text-white">new contacts only</span>. A contact already
                                    in HubSpot keeps the lead source it has.
                                </p>
                                {onEditFieldMapping && (
                                    <p className="mt-1.5">
                                        {writesOurProperty ? 'Using your own lead source property? ' : null}
                                        <button
                                            type="button"
                                            onClick={onEditFieldMapping}
                                            className="font-medium text-primary-700 dark:text-primary-400 underline underline-offset-2 hover:text-primary-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded"
                                        >
                                            Change where it's written in Field mapping
                                        </button>
                                    </p>
                                )}
                            </>
                        ) : (
                            <p>New contacts get no lead source from OrbitalX.</p>
                        )}
                    </div>
                </div>

                <div className="mt-3 flex items-start gap-2.5 px-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                    <Info className="w-4 h-4 mt-px shrink-0" aria-hidden="true" />
                    <p className="max-w-[68ch]">
                        <span className="font-medium text-slate-700 dark:text-slate-200">Original Traffic Source</span> is left to HubSpot.
                        It records these contacts as Offline sources › Integration › the name of the HubSpot app you connected, and keeps
                        whatever it already holds for an existing contact.
                    </p>
                </div>
            </div>

            {/* UTM write behavior */}
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h4 className="font-medium text-sm text-slate-900 dark:text-white">UTM write behavior</h4>
                        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Configure how UTM parameters are handled for new leads.</p>
                    </div>
                    <ToggleRow
                        checked={behavior.UtmWriteBehavior.Enabled}
                        onChange={() => updateBehavior({ UtmWriteBehavior: { ...behavior.UtmWriteBehavior, Enabled: !behavior.UtmWriteBehavior.Enabled } })}
                        ariaLabel="UTM write behavior"
                    />
                </div>
                {behavior.UtmWriteBehavior.Enabled && (
                    <RadioGroup
                        name="utmMode"
                        options={[
                            { value: 'Append', label: 'Append to existing UTM properties', description: 'If property has value, append new value separated by comma.' },
                            { value: 'Overwrite', label: 'Overwrite existing UTM properties', description: 'Always replace current value with new value.' },
                        ]}
                        value={behavior.UtmWriteBehavior.Mode}
                        onChange={(val) => updateBehavior({ UtmWriteBehavior: { ...behavior.UtmWriteBehavior, Mode: val as HubSpotUtmMode } })}
                    />
                )}
            </div>
        </div>
    )
}
